import * as React from 'react';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import snakeCase from 'lodash/snakeCase';

import { FormField } from 'components/form';
import Input from 'components/inputs/input/Input';

import { __t, applyTranslations } from 'utils/language';
import { insertIf } from 'utils/list';
import { isRequired, isUrl, isZalandoPage, maxLength } from 'utils/validators';

import './deliverable-landing-page-fields.scss';

applyTranslations(require('./deliverable-landing-page-fields.i18n.json'));

import type {
  Deliverable,
  TextEntry
} from 'types';

type FieldName = 'landingPageUrl' | 'landingPageDescription';

interface Props {
  deliverable: Deliverable,
  onInputChanged: (event: React.SyntheticEvent<TextEntry>) => void
}

const MSG_KEY = 'deliverable_landing_page_fields';
const MAX_FIELD_LENGTH = 250;

class DeliverableLandingPageFields extends React.PureComponent<Props> {
  renderInput(fieldName: FieldName): React.ReactNode {
    const { deliverable, onInputChanged } = this.props;

    return (
      <Input
        name={fieldName}
        value={deliverable[fieldName]}
        width="responsive"
        charCount={fieldName === 'landingPageDescription' ? {
          curCount: deliverable.landingPageDescription.length,
          maxChars: MAX_FIELD_LENGTH
        } : undefined}
        onChange={onInputChanged}
      />
    );
  }

  renderField(fieldName: FieldName): React.ReactNode {
    const { deliverable } = this.props;
    const numDeliverableLinks = isArray(deliverable.links)
      ? deliverable.links.length
      : 0;

    const urlValidators = (fieldName === 'landingPageUrl') ? [
      isUrl(deliverable.landingPageUrl),
      isZalandoPage(deliverable.landingPageUrl),
      ...insertIf(!isEmpty(deliverable.landingPageDescription), isRequired(deliverable.landingPageUrl))
    ] : [];

    return (
      <div className="deliverable-landing-page-fields__section">
        <FormField
          showValidationIcon={false}
          label={__t(`${MSG_KEY}.labels.${snakeCase(fieldName)}`)}
          labelType="highlighted"
          direction="column"
          fontSize="small"
          {
            ...fieldName === 'landingPageUrl'
              ? { tooltipMessage: __t(`${MSG_KEY}.tooltips.landing_page_url`) }
              : null
          }
          validators={[
            maxLength(deliverable[fieldName], MAX_FIELD_LENGTH),
            ...urlValidators
          ]}
        >
          {this.renderInput(fieldName)}
        </FormField>
        {
          // necessary to mimic `DeliverableLinksForm` behavior
          fieldName === 'landingPageDescription' && numDeliverableLinks > 1 && (
            <div className="deliverable-landing-page-fields__action-section">
              <div className="deliverable-landing-page-fields__placeholder" />
            </div>
          )
        }
      </div>
    );
  }

  render(): React.ReactNode {
    return (
      <div className="deliverable-landing-page-fields">
        {this.renderField('landingPageUrl')}
        {this.renderField('landingPageDescription')}
      </div>
    );
  }
}

export default DeliverableLandingPageFields;
